import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CircleDollarSign, RefreshCw, Wallet } from "lucide-react";

import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

/**
 * The payment run.
 *
 * This is the screen the whole feature rests on. A treasurer facing forty
 * individual claims goes back to a spreadsheet, so everything here is built
 * around one idea: **pay a person, not a claim**. Tap someone to select their
 * whole balance, drop individual lines if a receipt is still in question, and
 * record the lot against a single bank reference.
 *
 * It is a separate page from reviewing because it is a separate authority — the
 * backend allows only admins and sub-admins here, and this page enforces the
 * same rule rather than trusting the navigation.
 */

export default function ClaimsPaymentsPage() {
  const navigate = useNavigate();
  const { authAxios, isAdmin, isSubAdmin } = useAuth();
  const canPay = isAdmin || isSubAdmin;

  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [reference, setReference] = useState("");
  const [paying, setPaying] = useState(false);

  const load = useCallback(async () => {
    if (!canPay) { setLoading(false); return; }
    setLoading(true);
    try {
      const api = authAxios();
      const res = await api.get("/claims/payment-run");
      setRun(res.data);
    } catch {
      toast.error("Could not load the payment run");
    } finally {
      setLoading(false);
    }
  }, [authAxios, canPay]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const togglePerson = (person, on) =>
    setSelected((prev) => {
      const next = new Set(prev);
      person.claims.forEach((c) => (on ? next.add(c.claim_id) : next.delete(c.claim_id)));
      return next;
    });

  const selectedTotalCents = useMemo(() => {
    if (!run) return 0;
    let cents = 0;
    for (const person of run.people) {
      for (const c of person.claims) {
        if (selected.has(c.claim_id)) cents += Math.round(parseFloat(c.amount) * 100);
      }
    }
    return cents;
  }, [run, selected]);

  const pay = async () => {
    if (!selected.size) return toast.error("Select who you've paid");
    if (!reference.trim()) return toast.error("Enter a payment reference");
    setPaying(true);
    try {
      const api = authAxios();
      const res = await api.post("/claims/pay", {
        claim_ids: [...selected], reference: reference.trim(),
      });
      toast.success(`$${res.data.total} marked paid for ${res.data.people} people`);
      setSelected(new Set());
      setReference("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not record the payment");
    } finally {
      setPaying(false);
    }
  };

  if (!canPay) {
    return (
      <div className="p-8 max-w-md mx-auto text-center space-y-3" data-testid="payments-denied">
        <Wallet className="h-10 w-10 mx-auto text-muted-foreground" aria-hidden="true" />
        <p className="font-medium text-foreground">Payments are handled by an admin</p>
        <p className="text-sm text-muted-foreground">
          You can review and approve claims, but recording payment is a separate
          responsibility.
        </p>
        <Button variant="outline" onClick={() => navigate("/claims/review")}>
          Go to review
        </Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-4 md:p-8 w-full space-y-3">
        <div className="h-20 bg-muted rounded-xl animate-pulse" />
        <div className="h-64 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 w-full space-y-5 pb-24">
      <div className="flex items-center justify-between gap-2">
        <Button variant="ghost" size="sm" className="-ml-2 gap-1.5"
                onClick={() => navigate("/claims")} data-testid="payments-back">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Overview
        </Button>
        <Button variant="ghost" size="sm" onClick={load} aria-label="Refresh payment run"
                data-testid="payments-refresh">
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>

      <div>
        <h1 className="text-xl font-bold text-foreground">Payment run</h1>
        <p className="text-sm text-muted-foreground">
          Pay each person once, then record it here
        </p>
      </div>

      {!run || run.person_count === 0 ? (
        <Card>
          <CardContent className="p-8 text-center space-y-2" data-testid="payments-empty">
            <Wallet className="h-8 w-8 mx-auto text-muted-foreground" aria-hidden="true" />
            <p className="text-sm text-muted-foreground">
              Nothing approved and waiting to be paid.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">${run.grand_total}</strong> owed to{" "}
            {run.person_count} {run.person_count === 1 ? "person" : "people"} across{" "}
            {run.claim_count} claims.
          </p>

          <div className="space-y-2">
            {run.people.map((person) => {
              const ids = person.claims.map((c) => c.claim_id);
              const all = ids.every((id) => selected.has(id));
              return (
                <Card key={person.claimant_email}
                      className={all ? "border-primary/50" : ""}
                      data-testid={`payee-${person.claimant_email}`}>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-foreground truncate">
                          {person.claimant_name}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {person.claimant_email}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="font-bold text-foreground">${person.total}</span>
                        <Button size="sm" variant={all ? "default" : "outline"} className="h-10"
                                onClick={() => togglePerson(person, !all)}
                                data-testid={`select-${person.claimant_email}`}>
                          {all ? "Selected" : "Select"}
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-1 pt-1">
                      {person.claims.map((c) => (
                        <button key={c.claim_id} type="button" onClick={() => toggle(c.claim_id)}
                                aria-pressed={selected.has(c.claim_id)}
                                className={`w-full text-left text-xs rounded-md px-2 py-1.5 flex justify-between gap-2 ${
                                  selected.has(c.claim_id)
                                    ? "bg-primary/10 text-foreground"
                                    : "text-muted-foreground hover:bg-muted"}`}
                                data-testid={`claim-line-${c.claim_id}`}>
                          <span className="truncate">{c.description} · {c.event_title}</span>
                          <span className="font-medium shrink-0">${c.amount}</span>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <Card className="border-primary/40 sticky bottom-4">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm text-muted-foreground">Selected</p>
                <p className="font-bold text-foreground" data-testid="selected-total">
                  ${(selectedTotalCents / 100).toFixed(2)}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="payref" className="text-xs">Payment reference</Label>
                <Input id="payref" className="h-11" value={reference}
                       placeholder="Transfer id, cheque number, or 'cash'"
                       onChange={(e) => setReference(e.target.value)}
                       data-testid="payment-reference" />
                <p className="text-xs text-muted-foreground">
                  Pay in your bank first, then record it here. Nothing is transferred from
                  this app.
                </p>
              </div>
              <Button className="w-full h-12 gap-1.5" disabled={paying || !selected.size}
                      onClick={pay} data-testid="mark-paid">
                <CircleDollarSign className="h-4 w-4" aria-hidden="true" />
                Mark {selected.size} claim{selected.size === 1 ? "" : "s"} paid
              </Button>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
